
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Calendar, Users, Phone, CreditCard, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { HotelDetails } from '@/types/itinerary';
import { format } from 'date-fns';

interface HotelDetailsCardProps {
  hotel: HotelDetails;
}

export const HotelDetailsCard: React.FC<HotelDetailsCardProps> = ({ hotel }) => {
  // Parse dates for formatting
  const checkInDate = hotel.checkIn ? new Date(hotel.checkIn) : null;
  const checkOutDate = hotel.checkOut ? new Date(hotel.checkOut) : null;
  
  // Format dates if they exist
  const formattedCheckIn = checkInDate ? format(checkInDate, 'MMM d, yyyy') : 'Not specified';
  const formattedCheckOut = checkOutDate ? format(checkOutDate, 'MMM d, yyyy') : 'Not specified';
  
  return (
    <Card className="mb-6 border-purple-light/30 bg-white shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            <Building className="h-5 w-5 mr-2 text-purple-tertiary" />
            <CardTitle className="text-xl font-semibold text-purple-tertiary">
              {hotel.name}
            </CardTitle>
          </div>
          <Badge variant="outline" className="bg-purple-soft/30 text-purple-tertiary border-purple-light">
            Added by {hotel.addedBy}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-3">
          <div className="text-sm break-words">{hotel.address}</div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-purple-tertiary" />
              <div className="text-sm">
                <span className="font-medium">Check-in:</span> {formattedCheckIn}
              </div>
            </div>
            
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-purple-tertiary" />
              <div className="text-sm">
                <span className="font-medium">Check-out:</span> {formattedCheckOut}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {hotel.price && (
              <div className="flex items-center">
                <CreditCard className="h-4 w-4 mr-2 text-purple-tertiary" />
                <div className="text-sm">
                  <span className="font-medium">Price:</span> ${hotel.price}/night
                </div>
              </div>
            )}
            
            {hotel.phone && (
              <div className="flex items-center">
                <Phone className="h-4 w-4 mr-2 text-purple-tertiary" />
                <div className="text-sm">
                  <span className="font-medium">Phone:</span> {hotel.phone}
                </div>
              </div>
            )}
          </div>
          
          {hotel.confirmation && (
            <div className="flex items-center">
              <Tag className="h-4 w-4 mr-2 text-purple-tertiary" />
              <div className="text-sm">
                <span className="font-medium">Confirmation #:</span> {hotel.confirmation}
              </div>
            </div>
          )}
          
          {hotel.roommates && hotel.roommates.length > 0 && (
            <div className="pt-1">
              <div className="flex items-center mb-1">
                <Users className="h-4 w-4 mr-2 text-purple-tertiary" />
                <span className="text-sm font-medium">Roommates:</span>
              </div>
              <div className="flex flex-wrap gap-1 pl-6">
                {hotel.roommates.map(roommate => (
                  <Badge key={roommate.id} variant="secondary" className="bg-purple-soft/20 text-purple-tertiary">
                    {roommate.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {hotel.notes && (
            <div className="pt-1 border-t border-gray-100 mt-2">
              <div className="text-sm mt-2 whitespace-pre-wrap">{hotel.notes}</div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
